import React, { useContext } from "react";
import { ExternalInviteArgs } from "components/administration/invites/types";
import { FormProvider, useForm } from "react-hook-form";
import { filter, get, map } from "lodash";
import { NewInviteContext } from "components/administration/invites/NewInvitePage";
import DirectoryPickerInput from "components/shared/form/fields/DirectoryPickerInput";
import TextInput from "components/shared/form/fields/TextInput";
import FormActions from "components/shared/form/fields/FormActions";
import InviteErrors from "components/administration/invites/InviteErrors";
import { useMutation } from "react-query";
import { fetchApi } from "helpers/reactQueryApi";
import { useNavigate } from "helpers/tixxt-router";
import getContextUrl from "helpers/getContextUrl";

type FormArgs = {
  members: { id: string }[];
  additional_message: string;
};

export default function BatchMembershipInviteForm({
  errors,
}: ExternalInviteArgs) {
  const navigate = useNavigate();
  const context = useContext(NewInviteContext);

  const contextUrl = getContextUrl(context);

  const { mutate: inviteMemberships } = useMutation(
    (body: {
      membership_invites: {
        membership_ids: string[];
        group_ids: string[];
        additional_message: string;
      };
    }) =>
      fetchApi(`${contextUrl}/membership_invites`, { method: "POST", body }),
    {
      onSuccess: ({ message }: { message: string }) => {
        toastr.success(message);
        navigate(`${contextUrl}/administration/invites/sent`);
      },
    },
  );

  const methods = useForm<FormArgs>();
  const { handleSubmit } = methods;

  function submit(data: FormArgs) {
    const membership_ids = map(filter(data.members, { type: "member" }), "id");
    const group_ids = map(filter(data.members, { type: "group" }), "id");

    inviteMemberships({
      membership_invites: {
        membership_ids,
        group_ids,
        additional_message: data.additional_message,
      },
    });
  }

  return (
    <FormProvider {...methods}>
      <form className="form-horizontal" onSubmit={handleSubmit(submit)}>
        <div className="text-muted my-4">
          {context !== Preload.current_network &&
            I18n.t("js.invites.internal_group_invites.hint")}
          {get(context, "invites_restricted") &&
            I18n.t("js.invites.internal_group_invites.invites_restricted_hint")}
        </div>
        <InviteErrors {...errors} />
        <DirectoryPickerInput
          label={I18n.t("js.invites.memberships.label")}
          name="members"
          required
          multi
        />
        <TextInput
          label={I18n.t("js.invites.body")}
          name="additional_message"
          rows={10}
          multiline
        />
        <FormActions
          saveText={I18n.t("js.invites.submit")}
          showCancel={false}
        />
      </form>
    </FormProvider>
  );
}
